// crewbit-admin/src/pages/Money/Invoices.tsx
//
// SPEC: §Money.InvoicesListAdmin (filed 2026-05-04 during Tier 1 Phase C
// Phase B as gap 1 of 3 before §FinancialsPage.FinalRetire).
//
// Top-level admin page for /money/invoices. Composes PageHeader (with Export
// CSV + New Invoice actions) + InvoicesSummaryStrip + InvoicesSegmentChips
// + search input + InvoicesTable + load-more button + slide-over panels for
// detail and authoring.
//
// Refresh policy is OWNED by useInvoices (Supabase realtime subscription on
// the invoices table + 300ms search debounce). This page MUST NOT add
// visibility/window listeners of its own.
//
// Slide-over mount pattern matches Outstanding.tsx — page mounts
// MoneyInvoiceDetailPanel directly with invoiceId derived from
// ?invoice_id= URL param. Authoring panel mounted with local state +
// effect-driven URL action handling per FinancialsPage:54-67 precedent.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Download } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { Toast } from '../../components/common/Toast';
import { EmptyState } from '../../components/common/EmptyState';
import { InvoicesSummaryStrip } from '../../components/money/InvoicesSummaryStrip';
import { InvoicesSegmentChips } from '../../components/money/InvoicesSegmentChips';
import { InvoicesTable } from '../../components/money/InvoicesTable';
import type { InvoiceRowAction } from '../../components/money/InvoicesTableRow';
import { MoneyInvoiceDetailPanel } from '../../components/money/MoneyInvoiceDetailPanel';
import { MoneyInvoiceAuthoringPanel } from '../../components/money/MoneyInvoiceAuthoringPanel';
import SettingsGearLink from '../../components/money/settings/SettingsGearLink';
import { BulkActionToolbar } from '../../components/money/BulkActionToolbar';
import { BulkActionMount } from '../../components/money/BulkActionMount';
import type { BulkActionKind } from '../../components/money/BulkActionConfirmModal';
import type { BulkInvoiceEnvelope } from '../../lib/invoiceBulkMutations';
import { useAuthStore } from '../../stores/authStore';
import { useInvoices } from '../../hooks/useInvoices';
import { useSortState } from '../../hooks/useSortState';
import { admin, palette, radii, shadow, spacing, transition, typography } from '../../styles/tokens';
import type { Invoice, InvoiceRow } from '../../types';
import {
  ErrorBranch,
  LoadingBranch,
  NoBusinessBranch,
  RowActionModals,
  type RowModal,
} from './InvoicesPageBranches';

const PAGE_SIZE_INCREMENT = 25;

type ToastState = {
  msg: string;
  type: 'success' | 'error' | 'warning' | 'info';
} | null;

const pageStyle: React.CSSProperties = {
  padding: spacing.xl,
  background: admin.bg,
  minHeight: '100vh',
};

const cardStyle: React.CSSProperties = {
  background: palette.white,
  borderRadius: admin.cardRadius,
  border: `1px solid ${admin.border}`,
  boxShadow: shadow.card,
  overflow: 'hidden',
};

export function MoneyInvoicesPage() {
  const business = useAuthStore((s) => s.business);
  const businessId = business?.id;

  const [searchParams, setSearchParams] = useSearchParams();
  const sort = useSortState('created_at', 'desc');

  const [pageSize, setPageSize] = useState(PAGE_SIZE_INCREMENT);
  const [toast, setToast] = useState<ToastState>(null);
  const [rowModal, setRowModal] = useState<RowModal>(null);
  const [authoringOpen, setAuthoringOpen] = useState(false);
  const [resumeInvoiceId, setResumeInvoiceId] = useState<string | null>(null);

  // §Money.OverviewBulkOperations: selection state + active bulk action.
  // Reason + method state lives in <BulkActionMount> per Golden Rule #14.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkAction, setBulkAction] = useState<BulkActionKind | null>(null);

  const options = useMemo(
    () => ({
      from: 0,
      to: pageSize - 1,
      sortColumn: sort.column,
      sortDirection: sort.direction,
    }),
    [pageSize, sort.column, sort.direction],
  );

  const {
    invoices,
    totalCount,
    summary,
    segmentCounts,
    loading,
    error,
    refresh,
    filters,
    setFilters,
    exportCSV,
  } = useInvoices(businessId, options);

  const invoiceIdParam = searchParams.get('invoice_id');
  const actionParam = searchParams.get('action');

  useEffect(() => {
    document.title = 'CrewBit · Invoices';
  }, []);

  useEffect(() => {
    if (actionParam === 'new-invoice') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time URL action consumption (mirrors FinancialsPage:54-67)
      setAuthoringOpen(true);
      setResumeInvoiceId(null);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('action');
          return next;
        },
        { replace: true },
      );
    } else if (actionParam === 'edit-draft' && invoiceIdParam) {
      setAuthoringOpen(true);
      setResumeInvoiceId(invoiceIdParam);
    }
  }, [actionParam, invoiceIdParam, setSearchParams]);

  const handleRowClick = (id: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('invoice_id', id);
      return next;
    });
  };

  const handleDetailClose = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('invoice_id');
      return next;
    });
  };

  const handleEditFromDetail = (invoice: Invoice) => {
    if (invoice.status !== 'draft') return;
    setResumeInvoiceId(invoice.id);
    setAuthoringOpen(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('invoice_id');
      return next;
    });
  };

  const handleAuthoringClose = () => {
    setAuthoringOpen(false);
    setResumeInvoiceId(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('action');
        next.delete('invoice_id');
        return next;
      },
      { replace: true },
    );
    void refresh();
  };

  const handleActionClick = (invoice: InvoiceRow, action: InvoiceRowAction) =>
    setRowModal({ kind: action, invoice });

  // §Money.OverviewBulkOperations: selection handlers.
  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    setSelectedIds((prev) => {
      const allSelected = invoices.length > 0 && invoices.every((i) => prev.has(i.id));
      if (allSelected) {
        const next = new Set(prev);
        invoices.forEach((i) => next.delete(i.id));
        return next;
      }
      const next = new Set(prev);
      invoices.forEach((i) => next.add(i.id));
      return next;
    });
  };

  const handleClearSelection = () => setSelectedIds(new Set());
  const handleBulkActionClick = (action: BulkActionKind) => setBulkAction(action);
  const handleBulkClose = () => setBulkAction(null);

  const selectedInvoices = invoices.filter((i) => selectedIds.has(i.id));

  const handleBulkResult = (envelope: BulkInvoiceEnvelope) => {
    const { success_count, failed_count, total } = envelope;
    if (failed_count === 0) {
      setToast({
        msg: `${success_count} of ${total} invoice${total === 1 ? '' : 's'} processed successfully.`,
        type: 'success',
      });
      handleClearSelection();
      void refresh();
    } else {
      setToast({
        msg: `${success_count} succeeded, ${failed_count} failed of ${total}.`,
        type: 'warning',
      });
      void refresh();
      // Modal stays open showing failed list per BulkActionConfirmModal partial-failure path.
    }
  };

  const handleModalResult = (ok: boolean, message: string) => {
    setToast({ msg: message, type: ok ? 'success' : 'error' });
    if (ok) void refresh();
  };

  const handleSegmentChange = (segment: typeof filters.segment) => {
    setFilters({ ...filters, segment });
    setPageSize(PAGE_SIZE_INCREMENT);
  };

  const handleSearchChange = (value: string) => {
    setFilters({ ...filters, search: value });
    setPageSize(PAGE_SIZE_INCREMENT);
  };

  const handleNewInvoice = () => {
    setResumeInvoiceId(null);
    setAuthoringOpen(true);
  };

  // ?action=edit-draft suppresses detail-panel mount to avoid stacked
  // slide-overs; the authoring panel owns the invoice in that flow.
  const detailPanelInvoiceId = actionParam === 'edit-draft' ? null : invoiceIdParam;
  const detailPanel = (
    <MoneyInvoiceDetailPanel
      invoiceId={detailPanelInvoiceId}
      onClose={handleDetailClose}
      onEditAction={handleEditFromDetail}
    />
  );
  const authoringPanel = (
    <MoneyInvoiceAuthoringPanel
      isOpen={authoringOpen}
      onClose={handleAuthoringClose}
      resumeInvoiceId={resumeInvoiceId ?? undefined}
    />
  );

  const headerActions = (
    <>
      <button
        type="button"
        onClick={() => exportCSV()}
        style={secondaryButtonStyle}
        title="Export current view to CSV"
      >
        <Download size={14} />
        Export CSV
      </button>
      <button type="button" onClick={handleNewInvoice} style={primaryButtonStyle}>
        <Plus size={14} />
        New Invoice
      </button>
    </>
  );

  if (!businessId) {
    return <NoBusinessBranch detailPanel={detailPanel} authoringPanel={authoringPanel} />;
  }

  if (loading && invoices.length === 0 && !error) {
    return (
      <LoadingBranch
        headerActions={headerActions}
        detailPanel={detailPanel}
        authoringPanel={authoringPanel}
      />
    );
  }

  if (error && invoices.length === 0) {
    return (
      <ErrorBranch
        headerActions={headerActions}
        error={error}
        onRetry={() => refresh()}
        detailPanel={detailPanel}
        authoringPanel={authoringPanel}
      />
    );
  }

  // True empty (no invoices anywhere for this business) vs filtered empty
  // (invoices exist but none match current segment/search). Drives the
  // empty-state copy + whether to surface a "+ New Invoice" CTA inline.
  const isAccountEmpty = summary.invoiceCount === 0;
  const showLoadMore = totalCount !== null && invoices.length < totalCount;

  return (
    <div style={pageStyle}>
      <PageHeader
        title="Invoices" back={{ to: '/money', label: 'Money' }}
        primaryAction={headerActions}
        secondaryActions={<SettingsGearLink />}
      />

      <div style={{ marginBottom: spacing.lg }}>
        <InvoicesSummaryStrip summary={summary} />
      </div>

      <InvoicesSegmentChips
        segmentCounts={segmentCounts}
        activeSegment={filters.segment}
        onSegmentChange={handleSegmentChange}
      />

      <div style={{ display: 'flex', gap: spacing.sm, marginBottom: spacing.md, alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Search invoice #, client name, or email"
          value={filters.search}
          onChange={(e) => handleSearchChange(e.target.value)}
          style={searchInputStyle}
          aria-label="Search invoices"
        />
      </div>

      <BulkActionToolbar
        selectedInvoices={selectedInvoices}
        onClearSelection={handleClearSelection}
        onActionClick={handleBulkActionClick}
      />

      <div style={cardStyle}>
        {invoices.length === 0 ? (
          <div style={{ padding: spacing.xl }}>
            <EmptyState
              title={isAccountEmpty ? 'No invoices yet' : 'No invoices match your filters'}
              subtitle={
                isAccountEmpty
                  ? 'Create your first invoice to get started.'
                  : 'Try a different segment, search term, or clear filters.'
              }
              actionLabel={isAccountEmpty ? '+ New Invoice' : undefined}
              onAction={isAccountEmpty ? handleNewInvoice : undefined}
            />
          </div>
        ) : (
          <InvoicesTable
            invoices={invoices}
            sort={sort}
            onRowClick={handleRowClick}
            onActionClick={handleActionClick}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
          />
        )}
      </div>

      {showLoadMore && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: spacing.lg }}>
          <button
            type="button"
            onClick={() => setPageSize((s) => s + PAGE_SIZE_INCREMENT)}
            disabled={loading}
            style={loadMoreButtonStyle(loading)}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.background = palette.g50;
            }}
            onMouseLeave={(e) => {
              if (!loading) e.currentTarget.style.background = palette.white;
            }}
          >
            {loading ? 'Loading…' : `Load more (${invoices.length} of ${totalCount ?? '?'})`}
          </button>
        </div>
      )}

      {detailPanel}
      {authoringPanel}

      <RowActionModals
        rowModal={rowModal}
        onClose={() => setRowModal(null)}
        onResult={handleModalResult}
      />

      <BulkActionMount
        action={bulkAction}
        selectedInvoices={selectedInvoices}
        onClose={handleBulkClose}
        onResult={handleBulkResult}
      />

      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          isVisible={!!toast}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}

const primaryButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: radii.sm,
  border: 'none',
  background: palette.accent,
  color: palette.white,
  fontSize: typography.size.caption,
  fontWeight: typography.weight.semibold,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  color: palette.g700,
  fontSize: typography.size.caption,
  fontWeight: typography.weight.semibold,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const searchInputStyle: React.CSSProperties = {
  flex: 1,
  maxWidth: 360,
  padding: '8px 12px',
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  color: palette.g800,
  fontSize: typography.size.caption,
  fontFamily: 'inherit',
  outline: 'none',
};

const loadMoreButtonStyle = (loading: boolean): React.CSSProperties => ({
  padding: '10px 20px',
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  color: loading ? palette.g500 : palette.g700,
  fontSize: typography.size.caption,
  fontWeight: typography.weight.semibold,
  fontFamily: 'inherit',
  cursor: loading ? 'wait' : 'pointer',
  transition: `background ${transition.fast}`,
});
